import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CampaignCard } from "../components/CampaignCard.tsx";
import { LineItems } from "../components/LineItems.tsx";
import {
	type Campaign,
	type Standing,
	type StatsAnswer,
	UnauthorizedError,
	api,
} from "../lib/api.ts";
import { CLOSE_CARD, FIGURE_LABELS, OPEN_CARD, figureCellText } from "../lib/campaign-card-copy.ts";
import { type CardOpen, cardFromRow } from "../lib/campaign-card.ts";
import {
	CREATE_ANYWAY,
	CREATE_LOCKED,
	CREATE_LOCK_IS_LOCAL,
	EXISTS,
	LINE_ITEMS_HERE,
	MORE_BANNER,
	MORE_FAILED,
	READ_ONLY,
	UNKNOWN,
	budgetText,
	createdText,
	enablePrompt,
	fundingOptions,
	hostWarning,
	loadedLabel,
	mismatchText,
	servableText,
} from "../lib/campaign-copy.ts";
import { CANCEL, EDIT } from "../lib/campaign-manage-copy.ts";
import {
	CAMPAIGNS_PAGE_SIZE,
	CAMPAIGNS_PANEL,
	CARD_PANEL,
	CARD_PANEL_EMPTY,
	HIDE_NEW_CAMPAIGN,
	LINE_ITEMS_PANEL,
	LINE_ITEMS_PANEL_EMPTY,
	NEW_CAMPAIGN,
	NEW_CAMPAIGN_PANEL,
	START,
	startTitle,
} from "../lib/campaigns-layout-copy.ts";
import { asksFirst, cardBeside, formShownAfter } from "../lib/campaigns-layout.ts";
import {
	type CreateOutcome,
	type FoundById,
	type StatusOutcome,
	type TargetStatus,
	appendRows,
	campaignRequest,
	canWriteAccount,
	createOutcome,
	createThrew,
	foundById,
	mayCreateAgain,
	outcomeAfterReload,
	resetToFirstPage,
	statusOutcome,
	toggleTarget,
} from "../lib/campaigns.ts";
import { campaignLockKey, holdInFlight, inFlight, useHeldOutcome } from "../lib/create-locks.ts";
import { cardNotOnPage } from "../lib/overview-stats-copy.ts";
import {
	SPEND_LABEL,
	SPEND_NOTES,
	SPEND_READ_FAILED,
	billedCellText,
	coveredText,
	readAtText,
} from "../lib/stats-copy.ts";
import {
	accountCurrency,
	billedRowState,
	idsForStats,
	metricRowState,
	sevenDayWindow,
} from "../lib/stats.ts";
import { useLoad } from "../lib/useLoad.ts";
import { xHost } from "../lib/x-host.ts";
import {
	Badge,
	Button,
	InfoBanner,
	Input,
	Modal,
	Page,
	Panel,
	Select,
	Table,
} from "../ui/index.ts";

type Notice = { variant: "success" | "warning" | "danger"; title: string; body?: string };

function createNotice(o: CreateOutcome): Notice {
	switch (o.kind) {
		case "created":
			return { variant: "success", title: createdText(o.id, o.servable, o.reasonsNotServable) };
		case "exists":
			return {
				variant: "warning",
				title: o.id ? `${EXISTS.title} (${o.id})` : EXISTS.title,
				body: `${EXISTS.body} ${o.error}`,
			};
		case "unknown":
			return { variant: "warning", title: UNKNOWN.title, body: `${UNKNOWN.body} ${o.error}` };
		case "refused":
			return { variant: "danger", title: "Nothing was created.", body: o.error };
	}
}

function statusNotice(o: StatusOutcome): Notice {
	switch (o.kind) {
		case "set":
			return {
				variant: "success",
				title: `${o.id} is ${o.entityStatus}.`,
				body: servableText(o.servable, o.reasonsNotServable),
			};
		case "mismatch":
			return { variant: "danger", title: mismatchText(o.asked, o.reported), body: o.error };
		case "refused":
			return { variant: "danger", title: "The status was not changed.", body: o.error };
	}
}

const EMPTY_DRAFT = {
	name: "",
	fundingInstrumentId: "",
	dailyBudgetAmountLocalMicro: "",
	totalBudgetAmountLocalMicro: "",
};

/**
 * One ad account's campaigns, read live from X; a create that is always PAUSED; the
 * ACTIVE ↔ PAUSED switch. The rules are in `lib/campaigns.ts` and the words in
 * `lib/campaign-copy.ts`, where the runner reaches them; this renders.
 */
export function Campaigns({ standing }: { standing: Standing }) {
	const isPlatform = standing.standing === "platform";
	const navigate = useNavigate();
	/*
	 * «Open card» on the Overview's analytics (A8) lands here with the account and the
	 * campaign named: `?account=<id>&card=<campaign id>`. The account is chosen from it
	 * when it is one of the list; the card opens once its row is on the loaded page.
	 */
	const [params] = useSearchParams();
	const askedAccount = params.get("account");
	const askedCard = params.get("card");
	const [accounts] = useLoad(() => api.xAccounts());
	// A seated operator's grants come from the seat; a team's arrive with standing.
	const [access] = useLoad(() => (isPlatform ? api.myAccess() : Promise.resolve(null)));
	// Where a write goes, named on the page, beside Create and in the ACTIVE prompt. Unknown
	// until the smoke check answers, and treated as production until then.
	const [smoke] = useLoad(() => api.smoke());
	const host = xHost(smoke.state === "ready" ? smoke.data.x?.target : null);
	const warning = hostWarning(host);

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	const [accountId, setAccountId] = useState("");
	/*
	 * The seven-day window, built once per mount. A window rebuilt on every render would
	 * change mid-hour and make two figures on one screen answer two different questions.
	 */
	const [billedWindow] = useState(() => sevenDayWindow(new Date()));
	const account = list.find((a) => a.id === accountId) ?? null;
	const xAccountId = account?.xAccountId ?? "";

	useEffect(() => {
		if (!accountId && accounts.state === "ready" && accounts.data.accounts.length > 0) {
			const asked = accounts.data.accounts.find((a) => a.id === askedAccount);
			setAccountId((asked ?? accounts.data.accounts[0]).id);
		}
	}, [accountId, accounts, askedAccount]);

	const [campaigns, reloadCampaigns] = useLoad(() =>
		xAccountId ? api.campaigns(xAccountId) : Promise.resolve(null),
	);
	const [funding, reloadFunding] = useLoad(() =>
		xAccountId ? api.fundingInstruments(xAccountId) : Promise.resolve(null),
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a different account is the reason to read again
	useEffect(() => {
		reloadCampaigns();
		reloadFunding();
	}, [xAccountId, reloadCampaigns, reloadFunding]);

	/*
	 * Pages after the first, and the cursor for the next one.
	 *
	 * The first page stays with `useLoad` — the account select, the create lock and
	 * "Reload list" all drive it — and everything Load more brings back is held
	 * here beside it. Both are dropped whenever that first page is read again, so a
	 * reload, a new account or a create can never leave page two of the old list
	 * sitting under page one of the new.
	 */
	const [morePages, setMorePages] = useState<Campaign[]>([]);
	const [cursor, setCursor] = useState<string | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [moreError, setMoreError] = useState<string | null>(null);

	// `campaigns` is the whole dependency, and narrowing it to `xAccountId` is the
	// mutation that used to pass: every new first page — another account, a reload,
	// a create — arrives as a new object, and each one must reset both. The rule
	// itself is `resetToFirstPage`; `campaigns.test.ts` pins this call too.
	useEffect(() => {
		const reset = resetToFirstPage<Campaign>(
			campaigns.state === "ready" ? (campaigns.data ?? null) : null,
		);
		setMorePages(reset.rows);
		setMoreError(null);
		setCursor(reset.cursor);
	}, [campaigns]);

	const platformGrants = access.state === "ready" && access.data ? access.data.grants : [];
	const writable = account ? canWriteAccount(standing, platformGrants, account.id) : false;

	const [form, setForm] = useState(EMPTY_DRAFT);
	const [formError, setFormError] = useState<string | null>(null);
	/**
	 * The campaign whose line items are open, held as the row itself.
	 *
	 * One at a time, and never a boolean per row: a line item is the object that
	 * spends, and two open sections invite a create aimed at the wrong campaign.
	 */
	const [openLineItems, setOpenLineItems] = useState<Campaign | null>(null);
	/**
	 * The campaign whose card is open — everything read about it on one card
	 * (components/CampaignCard.tsx, 2026-10-21). One at a time, as the line items are,
	 * and a separate state from them: the card reads, the panel acts, and the card's
	 * own button opens the panel for the same campaign.
	 */
	const [openCard, setOpenCard] = useState<CardOpen | null>(null);
	const [notice, setNotice] = useState<Notice | null>(null);
	// The New campaign form takes the lower half while it is open (one-screen layout).
	const [showCreate, setShowCreate] = useState(false);
	// 🔴 Opening the card or the Line items panel — from a row, or from the card — gives the
	// lower half back; otherwise the press changes state that nothing shows (-da, #234).
	useEffect(() => {
		if (openCard !== null || openLineItems !== null)
			setShowCreate((shown) => formShownAfter("opened", shown));
	}, [openCard, openLineItems]);
	/*
	 * Whether a line item's own screen is open in the Line items panel: it then takes the
	 * lower half's width, and the card waits beside it unseen (`cardBeside`). 🔴 An opening
	 * of the card closes that screen — `closeLineItemScreen` counts the openings — for the
	 * reason an opening closes the form: a press must open something that is shown.
	 */
	const [lineItemScreen, setLineItemScreen] = useState(false);
	const [closeLineItemScreen, setCloseLineItemScreen] = useState(0);
	/*
	 * 🔴 The Line items panel's key, counted up when the card deleted one of that campaign's
	 * line items: the panel mounts again and reads X's list, so the deleted line item has no
	 * row and no Open there, and a screen open on it closes. Only then — a save leaves the
	 * panel as it is. The price, taken on purpose: pages read by Load more and a half-filled
	 * New line item form go with the old mount; the create lock does not (lib/create-locks.ts).
	 */
	const [lineItemsMount, setLineItemsMount] = useState(0);
	useEffect(() => {
		if (openCard !== null) setCloseLineItemScreen((n) => n + 1);
	}, [openCard]);
	const [busy, setBusy] = useState(false);
	// The campaign whose Start is being confirmed. The question names one campaign under
	// one account, so a confirmation still open when the account on screen changes would
	// answer for the other one — it goes with the account.
	const [starting, setStarting] = useState<Campaign | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a different account is the reason to close it
	useEffect(() => {
		setStarting(null);
	}, [xAccountId]);
	// The last create's answer, and with it the lock on Create. What lifts it is in
	// `outcomeAfterReload`: a read by id for an answer that carried one, and for the
	// rest only the deliberate "create anyway" below — never a list, which lags.
	// Held in `create-locks.ts` under this ad account, so it outlives this page — a
	// route away and back unmounted it, and the lock went with it — while a browser
	// reload still clears it; the copy says so rather than leaving that to be found by
	// pressing F5.
	const [outcome, setOutcome] = useHeldOutcome<CreateOutcome>(campaignLockKey(xAccountId));
	const [reloading, setReloading] = useState(false);
	// The second step of "create anyway": the sentence to agree to is ours, so the
	// dialog has to be ours too. Cleared whenever the lock is (see below).
	const [confirming, setConfirming] = useState(false);
	// 🔴 A create still out holds the lock too: this page may be a new mount after a route
	// away while it was out (lib/create-locks.ts, `holdInFlight`).
	const locked =
		inFlight(campaignLockKey(xAccountId)) || (outcome !== null && !mayCreateAgain(outcome));
	// Whatever lifted the lock — a read by id, a new create, "create anyway" itself —
	// the half-made confirmation goes with it, so the next lock does not open already
	// agreed to.
	useEffect(() => {
		if (!locked) setConfirming(false);
	}, [locked]);
	const field = (key: keyof typeof EMPTY_DRAFT) => (e: { target: { value: string } }) =>
		setForm((f) => ({ ...f, [key]: e.target.value }));

	// The account on screen now, so a list that arrives for another one lifts nothing.
	const chosen = useRef(xAccountId);
	chosen.current = xAccountId;

	/**
	 * Reload the rows, and — where there is an id — ask X about that one campaign.
	 *
	 * 🔴 The list does not decide the lock any more. It lags behind a create by
	 * minutes (twads#86), so "the list came back and the campaign is not in it" is
	 * the normal appearance of a campaign that exists; deciding on it is what let a
	 * second one be created (twads#87). The id read does not lag, and it is the only
	 * thing consulted here.
	 */
	async function reloadList() {
		const asked = xAccountId;
		// The answer this run is about. Everything below is applied only if it is still
		// the one on screen: an await sits in the middle, and clearing a lock that a
		// later create put there would be the same bug one step along.
		const about = outcome;
		setReloading(true);
		try {
			const id = about?.kind === "exists" ? about.id : null;
			const byId: FoundById = id
				? await api.campaign(id, asked).then(
						({ status, body }) =>
							chosen.current === asked ? foundById(status, body, id) : "unasked",
						// A read that did not come back establishes nothing — and "nothing
						// established" has to keep the lock, not lift it.
						() => "unasked",
					)
				: "unasked";
			setOutcome((o) => (o === about ? outcomeAfterReload(o, byId) : o));
			// The lock lifts only when X confirmed the campaign by id, and then the draft
			// describes something that certainly exists. Leaving it filled in is how the
			// screen offers to make it twice.
			if (byId === "found" && outcomeAfterReload(about, byId) === null) setForm(EMPTY_DRAFT);
			reloadCampaigns();
		} finally {
			setReloading(false);
		}
	}

	async function create(e: React.FormEvent) {
		e.preventDefault();
		// Enter in a field submits too; the lock holds for it as for the button.
		if (locked) return;
		const request = campaignRequest(xAccountId, form);
		if (!request.ok) {
			setFormError(request.error);
			return;
		}
		setFormError(null);
		setNotice(null);
		setBusy(true);
		const sentUnder = campaignLockKey(xAccountId);
		holdInFlight(sentUnder, true);
		try {
			const { status, body } = await api.createCampaign(request.value);
			const answered = createOutcome(status, body);
			setOutcome(answered);
			setNotice(createNotice(answered));
			if (answered.kind === "created") setForm(EMPTY_DRAFT);
			if (answered.kind === "created") setShowCreate((shown) => formShownAfter("created", shown));
			if (answered.kind !== "refused") reloadCampaigns();
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			const threw = createThrew(err instanceof Error ? err.message : String(err));
			setOutcome(threw);
			setNotice(createNotice(threw));
		} finally {
			setBusy(false);
			holdInFlight(sentUnder, false);
		}
	}

	function toggle(campaign: Campaign) {
		const target = toggleTarget(campaign.entityStatus);
		if (!target || !account) return;
		// ACTIVE is confirmed with the campaign, the account and the budgets in front of the
		// person — and what it does not switch: the campaign's line items. The question is
		// the Modal below; the switch is sent only from its Start. PAUSED asks nothing.
		if (asksFirst(target)) {
			setStarting(campaign);
			return;
		}
		void sendStatus(campaign, target);
	}

	async function sendStatus(campaign: Campaign, target: TargetStatus) {
		setNotice(null);
		setBusy(true);
		try {
			const { status, body } = await api.setCampaignStatus(campaign.id, {
				account: xAccountId,
				status: target,
			});
			setNotice(statusNotice(statusOutcome(target, status, body)));
			reloadCampaigns();
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setNotice({
				variant: "danger",
				title: "The status change got no answer — look before switching again.",
				body: err instanceof Error ? err.message : String(err),
			});
		} finally {
			setBusy(false);
		}
	}

	/**
	 * The next page from X, appended to what is already on screen.
	 *
	 * The cursor is X's own string, handed back as it arrived. The account is read
	 * before and checked after, exactly as `reloadList` does it: a page that lands
	 * after the operator has switched accounts belongs to the account it was asked
	 * for, and is dropped rather than mixed into another one's list.
	 */
	async function loadMore() {
		const asked = xAccountId;
		const at = cursor;
		if (!asked || at === null || loadingMore) return;
		setLoadingMore(true);
		setMoreError(null);
		try {
			const next = await api.campaigns(asked, at);
			if (chosen.current !== asked) return;
			setMorePages((held) => appendRows(held, next.campaigns));
			setCursor(next.page.nextCursor);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			// The cursor is kept: the same button retries the same page.
			setMoreError(err instanceof Error ? err.message : String(err));
		} finally {
			setLoadingMore(false);
		}
	}

	const firstPage = campaigns.state === "ready" && campaigns.data ? campaigns.data.campaigns : [];
	const rows = appendRows(firstPage, morePages);
	/*
	 * The card the Overview asked for, opened once — the first time its row is on the
	 * page. `cardAsked` remembers it was handled, so closing the card is not undone by
	 * the next render.
	 */
	const [cardAsked, setCardAsked] = useState(false);
	const askedRow = askedCard ? (rows.find((c) => c.id === askedCard) ?? null) : null;
	useEffect(() => {
		if (cardAsked || askedRow === null) return;
		setOpenCard(cardFromRow(null, askedRow, "card", writable));
		setCardAsked(true);
	}, [cardAsked, askedRow, writable]);
	const cardMissing =
		askedCard !== null && !cardAsked && campaigns.state === "ready" && askedRow === null;
	const instruments =
		funding.state === "ready" && funding.data ? funding.data.fundingInstruments : [];
	const options = fundingOptions(instruments);

	/*
	 * What these campaigns spent in the last seven days.
	 *
	 * 🔴 **The figure is cleared before every read, and that is the whole reason this is
	 * an effect rather than a fetch beside the render.** The rows under it change — a
	 * different account, Load more, a reload — and a figure left standing would sit
	 * against rows it was never about. The same shape as `setCriteria(null)` on the
	 * targeting panel, and the same defect it exists to prevent.
	 *
	 * The window is built once per mount: two reads in the same hour ask X the same
	 * question, which is what makes two figures comparable. `cut` is what X's twenty-id
	 * ceiling left out, said under the table rather than left as a blank column.
	 */
	const picked = idsForStats(rows.map((c) => c.id));
	const askedFor = picked.ids.join(",");
	const [billed, setBilled] = useState<StatsAnswer | null>(null);
	const [billedFailed, setBilledFailed] = useState<string | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: the account and the ids on screen ARE the subject; `window` is built once on purpose
	useEffect(() => {
		setBilled(null);
		setBilledFailed(null);
		if (!xAccountId || askedFor === "") return;
		api
			.stats(xAccountId, "CAMPAIGN", askedFor.split(","), billedWindow)
			.then(setBilled)
			.catch((e: unknown) => {
				if (e instanceof UnauthorizedError) return;
				setBilledFailed(e instanceof Error ? e.message : String(e));
			});
	}, [xAccountId, askedFor]);

	return (
		<Page
			fill
			title="Campaigns"
			description="Read live from X, one ad account at a time. A campaign is created PAUSED; switching it to ACTIVE is a separate step, and whether X serves it is shown beside the status."
			// Calm only for the sandbox, which the probe of 2026-09-18 proved is X's
			// (docs/START-HERE.md, x-host.ts). Everything else warns — production because it
			// is production, an unnamed or unread host because it could be anything.
			actions={<Badge variant={host.kind === "sandbox" ? "info" : "warning"}>{host.text}</Badge>}
		>
			<div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-3">
				<div className="mb-4 flex flex-wrap items-end gap-3">
					<div className="w-full max-w-md">
						<Select
							label="Ad account"
							value={accountId}
							onChange={(e) => {
								setAccountId(e.target.value);
								setNotice(null);
								setForm(EMPTY_DRAFT);
								// Another account's campaigns cannot carry this one's open section.
								setOpenLineItems(null);
								setOpenCard(null);
							}}
							placeholder="Choose an ad account"
							disabled={list.length === 0}
							options={list.map((a) => ({ value: a.id, label: `${a.name} · ${a.xAccountId}` }))}
						/>
					</div>
					{/*
					 * The wizard (pages/CreateAd.tsx, 2026-10-21): the four creates on one screen,
					 * in words. Offered where Create is — behind the same `writable` — and it
					 * carries the account chosen here.
					 */}
					{account && writable && (
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(`/campaigns/new?account=${encodeURIComponent(account.id)}`)}
						>
							Create ad
						</Button>
					)}
					{account && writable && (
						<Button
							type="button"
							variant="outline"
							onClick={() => setShowCreate((shown) => formShownAfter("toggle", shown))}
						>
							{showCreate ? HIDE_NEW_CAMPAIGN : NEW_CAMPAIGN}
						</Button>
					)}
				</div>
				<Panel title={CAMPAIGNS_PANEL}>
					{accounts.state === "failed" && (
						<InfoBanner variant="danger" title="Ad accounts could not be read.">
							{accounts.why}
						</InfoBanner>
					)}
					{notice && (
						<InfoBanner variant={notice.variant} title={notice.title}>
							{notice.body}
						</InfoBanner>
					)}
					{cursor !== null && (
						<InfoBanner variant="warning" title={MORE_BANNER.title}>
							{MORE_BANNER.body}
						</InfoBanner>
					)}
					{campaigns.state === "failed" && (
						<InfoBanner variant="danger" title="Campaigns could not be read.">
							{campaigns.why}
						</InfoBanner>
					)}
					<Table<Campaign>
						columns={[
							{
								id: "name",
								header: "Campaign",
								/*
								 * Card and Edit live in the campaign's own cell (spec rev. 2, 2026-10-23):
								 * as columns six and seven of a table that scrolls sideways they could sit
								 * past its right edge, and the owner still did not see Edit after #190.
								 * Edit opens this campaign's card in its edit form — the card's own form,
								 * saved by the card — and only where the card would offer Edit.
								 */
								accessor: (c) => (
									<>
										<div className="font-medium text-content">{c.name}</div>
										<div className="font-mono text-xs text-muted">{c.id}</div>
										<div className="mt-1 flex flex-wrap gap-1">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													setOpenCard((open) => cardFromRow(open, c, "card", writable))
												}
											>
												{openCard?.campaign.id === c.id ? CLOSE_CARD : OPEN_CARD}
											</Button>
											{writable && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() =>
														setOpenCard((open) => cardFromRow(open, c, "edit", writable))
													}
												>
													{EDIT}
												</Button>
											)}
										</div>
									</>
								),
							},
							{
								id: "status",
								header: "Status",
								accessor: (c) => (
									<Badge variant={c.entityStatus === "ACTIVE" ? "info" : "default"}>
										{c.entityStatus}
									</Badge>
								),
							},
							{
								id: "servable",
								header: "Served",
								accessor: (c) => (
									<span className={c.servable ? "text-content" : "text-muted"}>
										{servableText(c.servable, c.reasonsNotServable)}
									</span>
								),
							},
							{
								id: "daily",
								header: "Daily budget",
								accessor: (c) => (
									<span className="font-mono text-xs">
										{budgetText(c.dailyBudgetAmountLocalMicro, c.currency)}
									</span>
								),
							},
							{
								id: "total",
								header: "Total budget",
								accessor: (c) => (
									<span className="font-mono text-xs">
										{budgetText(c.totalBudgetAmountLocalMicro, c.currency)}
									</span>
								),
							},
							{
								id: "line-items",
								header: "",
								accessor: (c) => (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setOpenLineItems((open) => (open?.id === c.id ? null : c))}
									>
										{openLineItems?.id === c.id ? "Hide line items" : "Line items"}
									</Button>
								),
							},
							{
								id: "billed",
								header: SPEND_LABEL,
								accessor: (c) => {
									/*
									 * 🔴 One call, and no sentence of this screen's own. `billedRowState`
									 * tells the five states apart — including the two that a single `!row`
									 * used to merge, "never in the request" and "X did not mention it" — and
									 * `billedCellText` words them. Both halves have cases; a literal here
									 * would have neither.
									 */
									const state = billedRowState({
										id: c.id,
										asked: picked.ids,
										failed: billedFailed !== null,
										answer: billed,
									});
									return (
										<span
											className={
												state.kind === "figure" ? "text-sm text-content" : "text-xs text-muted"
											}
										>
											{billedCellText(state, accountCurrency(instruments))}
										</span>
									);
								},
							},
							{
								id: "urlClicks",
								header: FIGURE_LABELS.urlClicks,
								accessor: (c) => {
									// The same read, the same five states, the card's wording for a count
									// (twads#174): X's url_clicks under X's own name for it.
									const state = metricRowState(
										{ id: c.id, asked: picked.ids, failed: billedFailed !== null, answer: billed },
										"urlClicks",
									);
									return (
										<span
											className={
												state.kind === "figure" ? "text-sm text-content" : "text-xs text-muted"
											}
										>
											{figureCellText(state, "urlClicks", accountCurrency(instruments))}
										</span>
									);
								},
							},
							{
								id: "actions",
								header: "",
								accessor: (c) => {
									const target = toggleTarget(c.entityStatus);
									if (!writable || !target) return null;
									return (
										<Button variant="ghost" size="sm" disabled={busy} onClick={() => toggle(c)}>
											{target === "PAUSED" ? "Pause" : "Start"}
										</Button>
									);
								},
							},
						]}
						data={rows}
						dense
						pageSize={CAMPAIGNS_PAGE_SIZE}
						keyExtractor={(c) => c.id}
						failed={campaigns.state === "failed"}
						loading={Boolean(xAccountId) && campaigns.state === "loading"}
						emptyMessage={
							account
								? "No campaigns under this account."
								: "Choose an ad account to list its campaigns."
						}
					/>
					{account && campaigns.state === "ready" && rows.length > 0 && (
						<div className="mt-3 flex flex-wrap items-center gap-3">
							<span className="text-sm text-muted">
								{loadedLabel(rows.length, cursor !== null)}
							</span>
							{cursor !== null && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									loading={loadingMore}
									onClick={() => void loadMore()}
								>
									Load more
								</Button>
							)}
						</div>
					)}
					{account && campaigns.state === "ready" && (
						<p className="mt-2 text-sm text-muted">{LINE_ITEMS_HERE}</p>
					)}
					{moreError && (
						<InfoBanner variant="danger" title={MORE_FAILED}>
							{moreError}
						</InfoBanner>
					)}
					{/*
					 * 🔴 What a spend figure here is NOT, said once under the table rather than in
					 * every row: it is provisional on X's own two horizons, its window end is
					 * exclusive, it counts one placement, and its currency came from a different
					 * object. Four properties of the figure, not of one campaign — and the screen
					 * that shows the number is where a person decides what to do about it.
					 */}
					{billed && rows.length > 0 && (
						<div className="mt-3 space-y-1">
							<p className="text-xs text-muted">
								{SPEND_LABEL} · {readAtText(billed.readAt)} · {billed.window.start} to{" "}
								{billed.window.end} · {billed.placement}
							</p>
							{coveredText(picked.cut) && (
								<p className="text-xs text-muted">{coveredText(picked.cut)}</p>
							)}
							{SPEND_NOTES.map((note) => (
								<p key={note.slice(0, 24)} className="text-xs text-muted">
									{note}
								</p>
							))}
						</div>
					)}
					{billedFailed && (
						<InfoBanner variant="warning" title={SPEND_READ_FAILED}>
							{billedFailed}
						</InfoBanner>
					)}

					{cardMissing && askedCard && (
						<p className="mt-3 text-sm text-muted">{cardNotOnPage(askedCard, cursor !== null)}</p>
					)}
					{account && !writable && <p className="mt-6 text-sm text-muted">{READ_ONLY}</p>}
				</Panel>
				{showCreate && account && writable ? (
					<Panel title={NEW_CAMPAIGN_PANEL}>
						<form className="grid max-w-2xl gap-4" onSubmit={create}>
							<h2 className="text-sm font-semibold text-content">New campaign — created PAUSED</h2>
							{warning && (
								<InfoBanner variant="warning" title={warning.title}>
									{warning.body}
								</InfoBanner>
							)}
							{formError && <InfoBanner variant="danger" title={formError} />}
							<Input label="Name" value={form.name} onChange={field("name")} />
							<Select
								label="Funding instrument"
								value={form.fundingInstrumentId}
								onChange={field("fundingInstrumentId")}
								placeholder={
									funding.state === "loading"
										? "Reading funding instruments…"
										: options.length === 0
											? "None under this account can fund"
											: "Choose a funding instrument"
								}
								options={options}
							/>
							{funding.state === "failed" && (
								<InfoBanner variant="danger" title="Funding instruments could not be read.">
									{funding.why}
								</InfoBanner>
							)}
							<Input
								label="Daily budget (micro units)"
								inputMode="numeric"
								value={form.dailyBudgetAmountLocalMicro}
								onChange={field("dailyBudgetAmountLocalMicro")}
								helperText="A whole number, 0 or more, in micro units of the instrument's currency. Leave empty to send none."
							/>
							<Input
								label="Total budget (micro units)"
								inputMode="numeric"
								value={form.totalBudgetAmountLocalMicro}
								onChange={field("totalBudgetAmountLocalMicro")}
								helperText="A whole number, 0 or more, in micro units of the instrument's currency. Leave empty to send none."
							/>
							<div className="flex flex-wrap items-center gap-3">
								<Button type="submit" loading={busy} disabled={locked}>
									Create PAUSED
								</Button>
								{locked && (
									<>
										<span className="text-sm text-muted">
											{CREATE_LOCKED} {CREATE_LOCK_IS_LOCAL}
										</span>
										<Button
											type="button"
											variant="outline"
											size="sm"
											loading={reloading}
											onClick={() => void reloadList()}
										>
											Reload list
										</Button>
										{/*
										 * The way out where no read can settle it — its own two steps rather
										 * than `window.confirm`, because the browser names the buttons "OK"
										 * and "Cancel" and the sentence that has to be agreed to is
										 * `CREATE_ANYWAY.confirm`. A dialog whose wording we do not control
										 * cannot carry it (review of #89).
										 *
										 * And Create stays a third, separate press: a single button that
										 * both unlocks and creates is the one pressed while still reading.
										 */}
										{!confirming ? (
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => setConfirming(true)}
											>
												{CREATE_ANYWAY.label}
											</Button>
										) : (
											<span className="flex flex-wrap items-center gap-2">
												<span className="text-sm text-danger">
													<strong>{CREATE_ANYWAY.title}</strong> {CREATE_ANYWAY.body}
												</span>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => {
														setConfirming(false);
														setOutcome(null);
														setNotice(null);
													}}
												>
													{CREATE_ANYWAY.confirm}
												</Button>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => setConfirming(false)}
												>
													Keep the warning
												</Button>
											</span>
										)}
									</>
								)}
							</div>
						</form>
					</Panel>
				) : (
					<div
						className={`grid min-h-0 gap-3 ${cardBeside(lineItemScreen) ? "grid-cols-2" : "grid-cols-1"}`}
					>
						{/* Put aside, not unmounted: what is open in the card is still there when the screen closes. */}
						<div className={cardBeside(lineItemScreen) ? "contents" : "hidden"}>
							<Panel title={CARD_PANEL}>
								{account && openCard && (
									<CampaignCard
										// Keyed on the campaign, for the reason the targeting panel is: a new card
										// per row rather than one re-pointed, so no read of the previous row survives.
										key={`${openCard.campaign.id}:${openCard.opened}`}
										campaign={openCard.campaign}
										startEditing={openCard.editing}
										account={account}
										// Handed, not read again: the rule S2 holds for the line-item section.
										writable={writable}
										currency={accountCurrency(instruments)}
										onOpenLineItems={() => setOpenLineItems(openCard.campaign)}
										// A save or a delete under the campaign changes what the list's row says.
										onChanged={(after) => {
											reloadCampaigns();
											if (
												after === "line-item-delete" &&
												openLineItems?.id === openCard.campaign.id
											)
												setLineItemsMount((n) => n + 1);
										}}
										// X confirmed the campaign gone: the card closes and the list is read again.
										onDeleted={() => {
											setOpenCard(null);
											setOpenLineItems(null);
											reloadCampaigns();
										}}
									/>
								)}
								{!(account && openCard) && <p className="text-sm text-muted">{CARD_PANEL_EMPTY}</p>}
							</Panel>
						</div>
						<Panel title={LINE_ITEMS_PANEL}>
							{account && openLineItems && (
								<LineItems
									key={lineItemsMount}
									campaign={openLineItems}
									account={account}
									writable={writable}
									currency={accountCurrency(instruments)}
									onScreen={setLineItemScreen}
									closeScreen={closeLineItemScreen}
								/>
							)}
							{!(account && openLineItems) && (
								<p className="text-sm text-muted">{LINE_ITEMS_PANEL_EMPTY}</p>
							)}
						</Panel>
					</div>
				)}
			</div>
			<Modal
				open={starting !== null && account !== null}
				title={starting ? startTitle(starting.id) : ""}
				confirmLabel={START}
				cancelLabel={CANCEL}
				onConfirm={() => {
					const campaign = starting;
					setStarting(null);
					if (campaign) void sendStatus(campaign, "ACTIVE");
				}}
				onCancel={() => setStarting(null)}
			>
				{starting && account ? enablePrompt(starting, account, host) : null}
			</Modal>
		</Page>
	);
}
